/**
 * AdminPurgeUtility — Phase 20.15
 * ------------------------------------------------------------------
 * Admin-only panel that lets the league admin surgically remove stale
 * check_ins from the primary collection AND recompute every user's
 * active `points_total` from the remaining rows. Two-step flow to
 * prevent accidental data loss:
 *
 *   1. Pick a cutoff date (exclusive upper bound).
 *   2. [ DRY RUN ] hits the API with `dry_run=true` — returns counts.
 *   3. If the counts look right, [ PURGE & RECALCULATE ] opens a
 *      confirmation modal, then fires the same endpoint with
 *      `dry_run=false`.
 *
 * Aesthetic mirrors the Brag Board / Rogue Directive panels:
 *   • border + var(--stakes-border), font-anton header, label-mono meta
 *   • red primary CTA to signal destructive action
 */
import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function defaultCutoff() {
  // Default suggestion: first of the CURRENT month, so a click on
  // [ DRY RUN ] previews wiping everything from prior months.
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

function ConfirmModal({ open, cutoff, wouldDelete, onCancel, onConfirm, busy }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      data-testid="purge-confirm-modal"
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md border border-[var(--stakes-primary)] bg-[var(--stakes-surface)] p-6"
      >
        <div className="label-mono" style={{ color: "var(--stakes-primary)" }}>
          DESTRUCTIVE ACTION
        </div>
        <h4 className="font-anton text-3xl uppercase mt-2 leading-none">
          ARE YOU SURE?
        </h4>
        <p className="label-mono mt-4 leading-relaxed">
          THIS WILL PERMANENTLY DELETE{" "}
          <span className="text-white font-bold">{wouldDelete}</span>{" "}
          CHECK-IN{wouldDelete === 1 ? "" : "S"} DATED BEFORE{" "}
          <span className="text-white font-bold">{cutoff}</span> AND
          RECALCULATE EVERY PLAYER&rsquo;S ACTIVE SCORE.
        </p>
        <p className="label-mono mt-3 leading-relaxed">
          ALL-TIME POINTS AND PAST WINNERS ARE PRESERVED. ARCHIVED
          RECORDS ARE NOT AFFECTED.
        </p>
        <div className="mt-6 flex gap-3">
          <button
            onClick={onCancel}
            disabled={busy}
            className="flex-1 h-11 label-mono border border-[var(--stakes-border)] hover:border-white disabled:opacity-30"
            data-testid="purge-confirm-cancel"
          >
            [ CANCEL ]
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="flex-1 h-11 label-mono border border-[var(--stakes-primary)] text-[var(--stakes-primary)] hover:bg-[var(--stakes-primary)] hover:text-black disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="purge-confirm-execute"
          >
            {busy ? "[ PURGING… ]" : "[ CONFIRM PURGE ]"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminPurgeUtility({ leagueId }) {
  const [cutoff, setCutoff] = useState(defaultCutoff);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [purgeResult, setPurgeResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // Reset the confirmation trail whenever the cutoff changes so a stale
  // dry-run count can't be used to authorize a purge against a different date.
  const onCutoffChange = useCallback((v) => {
    setCutoff(v);
    setDryRunResult(null);
    setPurgeResult(null);
  }, []);

  const runDryRun = useCallback(async () => {
    if (!leagueId || !cutoff || busy) return;
    setBusy(true);
    setPurgeResult(null);
    try {
      const r = await api.adminPurgeCheckins(leagueId, cutoff, true);
      setDryRunResult(r);
      if (r?.would_delete === 0) {
        toast.info("NO STALE CHECK-INS FOUND FOR THIS CUTOFF");
      } else {
        toast.success(`${r.would_delete} STALE CHECK-IN${r.would_delete === 1 ? "" : "S"} FOUND`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Dry run failed";
      toast.error(String(detail).toUpperCase());
    } finally {
      setBusy(false);
    }
  }, [leagueId, cutoff, busy]);

  const executePurge = useCallback(async () => {
    if (!leagueId || !cutoff) return;
    setBusy(true);
    try {
      const r = await api.adminPurgeCheckins(leagueId, cutoff, false);
      setPurgeResult(r);
      setDryRunResult(null);
      setModalOpen(false);
      toast.success(
        `PURGED ${r.deleted} CHECK-IN${r.deleted === 1 ? "" : "S"} · ${r.recomputed?.length || 0} PLAYER${(r.recomputed?.length || 0) === 1 ? "" : "S"} RECOMPUTED`,
      );
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Purge failed";
      toast.error(String(detail).toUpperCase());
      setModalOpen(false);
    } finally {
      setBusy(false);
    }
  }, [leagueId, cutoff]);

  const canExecute = useMemo(
    () => !!dryRunResult && (dryRunResult.would_delete ?? 0) > 0 && !busy,
    [dryRunResult, busy],
  );

  return (
    <div
      className="mt-10 pt-8 border-t border-[var(--stakes-border)]"
      data-testid="admin-purge-utility"
    >
      <div className="label-mono">ADMIN TOOLS</div>
      <h3 className="font-anton text-2xl md:text-3xl uppercase mt-1 leading-none">
        PURGE CHECK-INS
      </h3>
      <p className="label-mono mt-3 leading-relaxed max-w-2xl">
        SURGICALLY DELETE STALE CHECK-INS FROM PRIOR PERIODS AND
        RECOMPUTE EVERY PLAYER&rsquo;S ACTIVE SCORE. USE THIS IF
        RECALCULATION KEEPS RESURRECTING OLD POINTS. ALL-TIME POINTS AND
        HALL OF FAME RECORDS ARE NEVER TOUCHED.
      </p>

      <div className="mt-6 flex flex-col md:flex-row md:items-end gap-3">
        <div className="flex-1 min-w-0">
          <label className="label-mono block mb-2" htmlFor="purge-cutoff-input">
            PURGE CHECK-INS BEFORE
          </label>
          <input
            id="purge-cutoff-input"
            data-testid="purge-cutoff-input"
            type="date"
            value={cutoff}
            onChange={(e) => onCutoffChange(e.target.value)}
            className="w-full h-11 px-3 bg-[var(--stakes-surface-2)] border border-[var(--stakes-border)] font-mono uppercase text-white focus:outline-none focus:border-white"
          />
        </div>
        <div className="flex gap-3">
          <button
            onClick={runDryRun}
            disabled={busy || !cutoff}
            className="h-11 px-4 label-mono border border-[var(--stakes-border)] hover:border-white hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="purge-dry-run-button"
          >
            {busy && !modalOpen ? "[ CHECKING… ]" : "[ DRY RUN ]"}
          </button>
          <button
            onClick={() => setModalOpen(true)}
            disabled={!canExecute}
            className="h-11 px-4 label-mono border border-[var(--stakes-primary)] text-[var(--stakes-primary)] hover:bg-[var(--stakes-primary)] hover:text-black disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="purge-execute-button"
          >
            [ PURGE & RECALCULATE ]
          </button>
        </div>
      </div>

      {/* Dry-run readout */}
      {dryRunResult && (
        <div
          className="mt-5 p-4 border border-[var(--stakes-border)] bg-[var(--stakes-surface-2)]"
          data-testid="purge-dry-run-result"
        >
          <div className="label-mono">DRY RUN · CUTOFF {dryRunResult.before}</div>
          <div className="mt-2 font-anton text-3xl uppercase leading-none">
            {dryRunResult.would_delete} CHECK-IN{dryRunResult.would_delete === 1 ? "" : "S"} WILL BE DELETED
          </div>
          <div className="label-mono mt-2">
            {dryRunResult.would_remain} WILL REMAIN AFTER THE PURGE
          </div>
        </div>
      )}

      {/* Post-purge report — deltas per player */}
      {purgeResult && (
        <div
          className="mt-5 p-4 border border-[var(--stakes-border)] bg-[var(--stakes-surface-2)]"
          data-testid="purge-result-report"
        >
          <div className="label-mono">PURGE COMPLETE · CUTOFF {purgeResult.before}</div>
          <div className="mt-2 font-anton text-3xl uppercase leading-none">
            {purgeResult.deleted} CHECK-IN{purgeResult.deleted === 1 ? "" : "S"} DELETED
          </div>
          <div className="label-mono mt-2">
            {purgeResult.remaining} REMAINING · {purgeResult.recomputed?.length || 0} PLAYERS RECOMPUTED
          </div>
          {Array.isArray(purgeResult.recomputed) && purgeResult.recomputed.length > 0 && (
            <div className="mt-3 divide-y divide-[var(--stakes-border)]">
              {purgeResult.recomputed.map((row) => (
                <div
                  key={row.user_id}
                  className="py-2 flex items-center justify-between gap-3 font-mono uppercase"
                  data-testid={`purge-recompute-row-${row.user_id}`}
                >
                  <div className="font-bold truncate">{row.name}</div>
                  <div className="tabular-nums flex items-center gap-3 flex-shrink-0">
                    <span className="text-[var(--stakes-muted)]">{row.before}</span>
                    <span className="text-[var(--stakes-muted)]">→</span>
                    <span className="text-white font-bold">{row.after}</span>
                    <span
                      className="label-mono w-14 text-right"
                      style={{
                        color: row.delta === 0
                          ? "var(--stakes-muted)"
                          : row.delta > 0
                          ? "#22c55e"
                          : "var(--stakes-primary)",
                      }}
                    >
                      {row.delta > 0 ? "+" : ""}{row.delta}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <ConfirmModal
        open={modalOpen}
        cutoff={dryRunResult?.before || cutoff}
        wouldDelete={dryRunResult?.would_delete ?? 0}
        onCancel={() => setModalOpen(false)}
        onConfirm={executePurge}
        busy={busy}
      />
    </div>
  );
}
